import { z } from 'zod';
import { pageQuerySchema } from './http.js';
/**
 * `GET /api/web/audit-events` — a tenant's own audit trail (S42-m4dr Phase 4,
 * `platform.audit.read`). TENANT-scoped: the DTO never carries `tenantId` — the caller's own
 * token already names it (docs/008 S1), and this endpoint never answers for any other tenant.
 */
/**
 * Who caused the row.
 *
 * - `member` — an ordinary tenant user, resolved by `actorUserId` off `AuditEvent`.
 * - `platform_operator` — an InstaCheckin staff member (`User.isPlatformOperator`), reading or
 *   acting on this tenant from outside it (docs/005 "Platform operators"). The console badges
 *   this kind visibly — a tenant should always be able to tell an InstaCheckin action from one of
 *   its own members'.
 * - `device` — a paired kiosk acting on its own authority (e.g. a future self-check-out audit
 *   trail). Reserved for forward compatibility; no write in this milestone emits it yet.
 * - `system` — no human or device principal: `actorUserId` is `null` on the row (a scheduled
 *   job, or an actor whose `User` row no longer exists).
 * - `partner` — partner staff acting through a setup session (adr/0035 d.7). `name` is the staff
 *   user, `partnerName` the firm; the console renders "Changed by <partnerName>".
 */
export const AUDIT_ACTOR_KINDS = [
    'member',
    'platform_operator',
    'device',
    'system',
    'partner',
];
export const auditActorKindSchema = z.enum(AUDIT_ACTOR_KINDS);
/**
 * `name`/`email` are present only "when resolvable" — the acting `User` row still exists and
 * was looked up. Absent for `system`, and absent for a `member`/`platform_operator` whose row
 * has since been deleted (the id survives on the audit row by design; the identity does not).
 */
export const auditActorSchema = z.object({
    kind: auditActorKindSchema,
    name: z.string().min(1).optional(),
    email: z.string().min(1).optional(),
    /** Only for `kind: 'partner'`, when the partner still exists. */
    partnerName: z.string().min(1).optional(),
});
/** What the action was taken on. `type` mirrors `AuditEvent.targetType` (e.g. `'Location'`,
 *  `'Tenant'`), never translated here — the console maps it, same as `action`. */
export const auditTargetSchema = z.object({
    type: z.string().min(1),
    id: z.string().min(1),
});
/**
 * A single scalar, JSON-safe metadata value. `AuditEvent.metadata` is a per-action allow-list
 * of scalars on write (docs/008 S7 — "never a spread row, never PII") and this is its wire
 * shape: the server additionally allow-lists which KEYS ever leave the process
 * (`apps/api/src/modules/platform/audit/dto.ts`), so this schema only needs to bound the VALUE
 * shape, not re-derive the key allow-list — that would be a second copy of it.
 */
export const auditMetadataValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const auditMetadataSchema = z.record(z.string(), auditMetadataValueSchema);
export const auditEventDTOSchema = z.object({
    id: z.string().min(1),
    /** Dotted verb phrase, e.g. `location.created`, `member.roles_changed`,
     *  `platform.operator.tenant_read`. Open vocabulary — the console renders a known one as
     *  translated human text and falls back to the raw string for an unknown one. */
    action: z.string().min(1),
    occurredAt: z.string().datetime(),
    actor: auditActorSchema,
    target: auditTargetSchema,
    metadata: auditMetadataSchema,
    /** Client IP of the authenticated request that wrote the row; `null` for jobs, older rows,
     *  and rows by a partner's staff or InstaCheckin support, whose network is not the tenant's
     *  to see (docs/008 S7 as amended). */
    ipAddress: z.string().nullable(),
    /** The same request's user agent, under the same rules as `ipAddress`. */
    userAgent: z.string().nullable(),
});
/**
 * `?page=&pageSize=&sort=` (from `pageQuerySchema`) plus the filters this list supports.
 * `action` is a PREFIX match (e.g. `action=member.` matches every member-role event), never a
 * full-vocabulary enum — the vocabulary is open by design (see `auditEventDTOSchema` above).
 */
export const auditEventListQuerySchema = pageQuerySchema.extend({
    /** Prefix match against `AuditEvent.action`. */
    action: z.string().trim().min(1).max(128).optional(),
    /** Exact match against `AuditEvent.actorUserId`. */
    actorUserId: z.string().trim().min(1).max(64).optional(),
    /** Inclusive lower bound on `occurredAt`. */
    from: z.string().datetime().optional(),
    /** Inclusive upper bound on `occurredAt`. */
    to: z.string().datetime().optional(),
});
/** `GET /api/web/audit-events/:id`. */
export const auditEventIdParamsSchema = z.object({ id: z.string().trim().min(1).max(64) });
/**
 * `GET /api/web/audit-events/export` — the list's filters without paging: the export takes every
 * matching row inside the plan's window, up to a server-side row cap.
 */
export const auditEventExportQuerySchema = auditEventListQuerySchema.omit({
    page: true,
    pageSize: true,
    sort: true,
});
/**
 * `GET /api/web/audit-events/access` — what the caller's plan lets them do with the trail.
 * Readable with `platform.audit.read` alone, because most holders of that permission cannot read
 * billing: the page needs it to choose between the trail and an upgrade prompt.
 */
export const auditLogAccessDTOSchema = z.object({
    /** `platform.audit_log` — false means the page shows an upgrade prompt instead of the trail. */
    entitled: z.boolean(),
    /** `platform.audit_log_days`; `null` = no window (the whole history) — also when the key is
     *  disabled, which is how plans without the log carry it. */
    windowDays: z.number().int().min(0).nullable(),
    /** `platform.audit_log_export`. */
    canExport: z.boolean(),
});
//# sourceMappingURL=audit-event.js.map